import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import addMilliseconds from 'date-fns/addMilliseconds';
import format from 'date-fns/format';
import pluralize from 'pluralize';
import { AutoSizer, Grid } from 'react-virtualized';
import { useMediaQuery } from 'react-responsive';

import Button from '../../../../../../../../components/global/Button';
import PauseButtonIcon from '../../../../../../../../components/icons/PauseButtonIcon';
import PlayButtonIcon from '../../../../../../../../components/icons/PlayButtonIcon';
import SlideshowThumbnail from './components/SlideshowThumbnail';
import { calculateSlideshowDuration } from '../../helpers';
import RangeSlider from '../../../../../../../../components/global/RangeSlider';
import { breakpointDown } from '../../../../../../../../services/responsive/responsive';

const propTypes = {
  slideshowPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  onPlayToggle: PropTypes.func.isRequired,
  isPlaying: PropTypes.bool,
  currentPhotoId: PropTypes.string,
  currentSlideDuration: PropTypes.number,
  currentSlideRemainingTime: PropTypes.number,
  elapsedTime: PropTypes.number,
  totalDuration: PropTypes.number,
  defaultDuration: PropTypes.number.isRequired,
  onDefaultDurationChange: PropTypes.func.isRequired,
  onThumbnailClick: PropTypes.func,
  onAddPhoto: PropTypes.func.isRequired,
  onDeletePhoto: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};
const SlideshowThumbnailMenu = ({
  slideshowPhotos,
  photosTotal,
  photosOffset,
  onPlayToggle,
  isPlaying,
  currentPhotoId,
  currentSlideDuration,
  currentSlideRemainingTime,
  elapsedTime = 0,
  totalDuration = 0,
  defaultDuration,
  onDefaultDurationChange,
  onThumbnailClick,
  onAddPhoto,
  onDeletePhoto,
  loadingMore,
  onLoadMore,
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const currentDefaultDurationTimeout = useRef();
  const [currentDefaultDuration, setCurrentDefaultDuration] = useState(
    defaultDuration,
  );
  useEffect(() => {
    setCurrentDefaultDuration(defaultDuration);
  }, [defaultDuration]);

  useEffect(() => {
    if (currentDefaultDuration !== defaultDuration) {
      if (currentDefaultDurationTimeout.current) {
        clearTimeout(currentDefaultDurationTimeout.current);
      }
      currentDefaultDurationTimeout.current = setTimeout(
        () => onDefaultDurationChange(currentDefaultDuration),
        500,
      );
    }

    return () => {
      if (currentDefaultDurationTimeout.current) {
        clearTimeout(currentDefaultDurationTimeout.current);
      }
    };
  }, [currentDefaultDuration]);

  const elapsedTimeDate = addMilliseconds(
    new Date('2021-01-01T00:00:00.000Z'),
    elapsedTime,
  );
  const totalDurationDate = addMilliseconds(
    new Date('2021-01-01T00:00:00.000Z'),
    totalDuration,
  );

  const currentPhotoIndex = slideshowPhotos.findIndex(
    ({ asset_image, photo }) =>
      asset_image?.id === currentPhotoId || photo?.id === currentPhotoId,
  );

  const onSectionRendered = ({ columnOverscanStopIndex }) => {
    if (!onLoadMore || loadingMore) {
      return;
    }

    if (slideshowPhotos.length === photosTotal) {
      return;
    }

    if (
      photosOffset >= slideshowPhotos.length ||
      columnOverscanStopIndex + 1 + photosOffset >= slideshowPhotos.length
    ) {
      onLoadMore();
    }
  };

  const cellRenderer = ({ columnIndex, key, style }) => {
    const slideshowPhoto = slideshowPhotos[columnIndex];
    const isActive =
      currentPhotoId &&
      (currentPhotoId === slideshowPhoto.photo?.id ||
        currentPhotoId === slideshowPhoto.asset_image?.id);

    return (
      <SlideshowThumbnail
        key={key}
        slideshowPhoto={slideshowPhoto}
        style={style}
        isActive={isActive}
        isSeen={
          elapsedTime >=
          calculateSlideshowDuration(
            slideshowPhotos,
            defaultDuration,
            columnIndex + 1,
          )
        }
        slideDuration={
          currentPhotoId === slideshowPhoto.photo?.id ||
          currentPhotoId === slideshowPhoto.asset_image?.id
            ? currentSlideDuration
            : undefined
        }
        slideRemainingTime={
          currentPhotoId === slideshowPhoto.photo?.id ||
          currentPhotoId === slideshowPhoto.asset_image?.id
            ? currentSlideRemainingTime
            : undefined
        }
        onClick={() => {
          onThumbnailClick({
            photoId: slideshowPhoto.asset_image?.id ?? slideshowPhoto.photo?.id,
            photoIdx: isActive ? undefined : columnIndex,
          });
        }}
        onDelete={
          isPlaying
            ? undefined
            : () =>
                onDeletePhoto({
                  photoId:
                    slideshowPhoto.asset_image?.id ?? slideshowPhoto.photo?.id,
                  photoIdx: columnIndex,
                  isIntroSlide: !!slideshowPhoto.asset_image,
                })
        }
      />
    );
  };

  return (
    <div className="slideshow-designer-thumbnail-menu-wrapper">
      <div className="w-100 d-flex d-md-none text-sm mb-1">
        {photosTotal} {pluralize('photo', photosTotal)}{' '}
        <Button
          btnSize="sm"
          btnStyle="link"
          className="text-extra-bold ml-2"
          onClick={onAddPhoto}
        >
          Add or remove photos
        </Button>
      </div>
      <div className="w-100 d-flex d-md-none align-items-center text-sm text-nowrap">
        Delay between photos
        <RangeSlider
          className="mx-2"
          onChange={values => setCurrentDefaultDuration(1000 * values[0])}
          values={[currentDefaultDuration / 1000]}
          min={0}
          max={10}
        />
        <span className="text-light">
          {currentDefaultDuration / 1000}{' '}
          {currentDefaultDuration === 1000 ? 'sec' : 'secs'}
        </span>
      </div>
      <div className="w-100 d-flex align-items-end">
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column align-items-center mx-1 mx-md-2">
            <Button
              btnStyle="link"
              onClick={onPlayToggle}
              className="slideshow-designer-play-btn"
            >
              {isPlaying ? (
                <PauseButtonIcon size={48} color="#000" />
              ) : (
                <PlayButtonIcon size={48} color="#000" />
              )}
            </Button>
            <p className="text-center text-xs mb-0">
              {' '}
              {format(elapsedTimeDate, 'mm:ss')}/
              {format(totalDurationDate, 'mm:ss')}
            </p>
          </div>
        </div>
        <div className="slideshow-designer-thumbnail-menu-container">
          <div>
            <div className="w-100 d-flex align-items-center mb-1">
              <p className="d-none d-md-inline-flex text-dark mb-0">
                {photosTotal} {pluralize('photo', photosTotal)}{' '}
                {photosTotal > 0 && (
                  <Button
                    btnStyle="link"
                    onClick={onAddPhoto}
                    className="text-extra-bold ml-1"
                  >
                    Add or remove photos
                  </Button>
                )}
              </p>
              <div className="d-none d-md-flex align-items-center ml-auto text-nowrap">
                Delay between photos
                <RangeSlider
                  className="mx-2"
                  onChange={values =>
                    setCurrentDefaultDuration(1000 * values[0])
                  }
                  values={[currentDefaultDuration / 1000]}
                  min={0}
                  step={0.5}
                  max={10}
                />
                <span className="text-light">
                  {(currentDefaultDuration / 1000).toFixed(1)} sec.
                </span>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="slideshow-designer-vertical-separator" />
            {/* Width will equal AutoSizer width - the separator (8px) */}
            <AutoSizer disableHeight height={isMobile ? 66 : 88}>
              {({ width }) => (
                <Grid
                  rowCount={1}
                  width={width - 8}
                  height={isMobile ? 66 : 88}
                  rowHeight={isMobile ? 50 : 72}
                  columnWidth={isMobile ? 73 : 110}
                  columnCount={slideshowPhotos.length}
                  cellRenderer={cellRenderer}
                  className="slideshow-designer-thumbnail-menu"
                  containerStyle={{ overflow: 'visible' }}
                  scrollToColumn={currentPhotoIndex}
                  onSectionRendered={onSectionRendered}
                />
              )}
            </AutoSizer>
          </div>
        </div>
      </div>
    </div>
  );
};

SlideshowThumbnailMenu.propTypes = propTypes;

export default SlideshowThumbnailMenu;
